/*
  La Sura - the "ready by" line on the product page (blocks/lasura-ready-by.liquid), and its
  short form in the sticky add to basket bar ([data-lasura-ready-short]).

  Earliest day = today (or tomorrow once the cutoff has passed) plus the lead days, moved past
  closed weekdays and closed dates. Always London time, because the kitchen is in SE4. Dates
  are counted at noon UTC so a clock change never shifts a day.

  Quiet by design: no live region, so a screen reader isn't interrupted. The line the server
  rendered ("Needs 5 days' notice.") stays if anything here fails. The date rule itself is
  shared with the party planner: assets/lasura-kitchen-dates.js.

  Also each product in the search sheet (snippets/lasura-search-ready.liquid, 2026-10-14), with
  data-format="short": "Ready from Fri 16 Oct", which fits a result row.

  Also the basket's "When" line (snippets/lasura-basket-method.liquid), in the cart drawer and on
  the cart page. Horizon morphs those in place: the drawer hydrates just after load, and both
  re-render after every basket change. A morph puts the server's line back, and can change
  data-days, on this same element, so connectedCallback doesn't run again. A MutationObserver
  re-reads the element and puts the date back each time.
*/
import { DAY, londonNow, closures, isClosed, isNearly, earliest } from '@theme/lasura-kitchen-dates';

class LasuraReadyBy extends HTMLElement {
  connectedCallback() {
    // Shopify sets lang="en", which Intl reads as US order ("Saturday, October 3"). A UK
    // kitchen: day before month, the same rule as lasura-party-planner.js.
    const l = document.documentElement.lang || 'en';
    const lang = l === 'en' ? 'en-GB' : l;
    this.format = new Intl.DateTimeFormat(lang, {
      timeZone: 'UTC',
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
    this.formatShort = new Intl.DateTimeFormat(lang, {
      timeZone: 'UTC',
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
    /* A microtask, so it runs straight after the morph, even in a background tab (unlike
       requestAnimationFrame). Our own write below triggers it once more and changes nothing. */
    this.observer = new MutationObserver(() => this.update());
    this.observer.observe(this, { subtree: true, childList: true, characterData: true, attributes: true });
    /* Horizon redraws the sticky bar for a new size, which blanked its short line until the next
       minute (2026-10-29, on Live too); fill it again once the redraw is in. */
    this.onVariant = () => setTimeout(() => this.update(), 0);
    document.addEventListener('variant:update', this.onVariant);
    this.update();
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.observer?.disconnect();
    document.removeEventListener('variant:update', this.onVariant);
  }

  update() {
    clearTimeout(this.timer);
    /* Read fresh every time: a morph can swap the line or change the days. */
    this.line = this.querySelector('.lasura-ready__line');
    this.days = Number(this.dataset.days);
    this.hour = Number(this.dataset.hour);
    if (!this.line || !Number.isFinite(this.days) || !Number.isFinite(this.hour)) return;
    this.shut = closures(this.dataset);

    const { today, seconds } = londonNow();
    const beforeCutoff = seconds < this.hour * 3600;
    const t = earliest(this.days, this.hour, this.shut);
    /* A year of closures is the most this can skip; past that, keep the server's line. */
    if (isClosed(t, this.shut)) return;

    let date = this.format.format(t);
    let dateShort = this.formatShort.format(t);
    if (t === today && this.dataset.today) date = dateShort = this.dataset.today;
    else if (t === today + DAY && this.dataset.tomorrow) date = dateShort = this.dataset.tomorrow;

    /* The quick add sheet's "Order by 2pm today" line (.lasura-ready__cutoff): only while
       ordering now, not after the cutoff, actually makes this date. A closed day can make the
       two the same. Written only on a change, as below. */
    const cutoff = this.querySelector('.lasura-ready__cutoff');
    const hide = !beforeCutoff || earliest(this.days, 0, this.shut) === t;
    if (cutoff && cutoff.hidden !== hide) cutoff.hidden = hide;

    const template = beforeCutoff ? this.dataset.before : this.dataset.after;
    /* Only on a change: writing the same text would wake the observer again, for ever. */
    /* A nearly booked earliest day says so (data-nearly-line, e.g. "{date} is nearly booked."). */
    const nearly = this.dataset.nearlyLine && isNearly(t, this.shut) ? ` ${this.dataset.nearlyLine.replace('{date}', date)}` : '';
    const text = template ? template.replace('{date}', this.dataset.format === 'short' ? dateShort : date) + nearly : '';
    if (text && this.line.textContent !== text) this.line.textContent = text;

    /* The sticky add to basket bar, if the theme has one on this page. */
    if (this.dataset.short) {
      for (const el of document.querySelectorAll('[data-lasura-ready-short]')) {
        el.textContent = this.dataset.short.replace('{date}', dateShort);
        el.hidden = false;
      }
    }

    /* Check again on the next minute, so the line changes at the cutoff and at midnight. */
    this.timer = setTimeout(() => this.update(), (60 - new Date().getSeconds()) * 1000);
  }
}

if (!customElements.get('lasura-ready-by')) customElements.define('lasura-ready-by', LasuraReadyBy);
